// Turns the local holdings into the context block sent to the experts, in the UI language.
// Amounts are expressed as weights and P&L percentages; portfolio names are not sent.
import { holdingLabel, t } from '@/i18n';
import { formatNYClock, todayNY } from '@/market/marketHours';
import { computeTotals, valueAsset } from '@/market/value';
import { heldOnly, type Asset, type FxRates, type Quote } from '@/types';

export function buildHoldingsContext(allAssets: Asset[], quotes: Record<string, Quote | undefined>, fx: FxRates | null): string {
  // Watch items (quantity 0) are not holdings: they are never described to the model, not even by name.
  const assets = heldOnly(allAssets);
  const totals = computeTotals(assets, quotes, fx);
  if (totals.totalUsd <= 0) return t('ctx.empty');
  const pct = (v: number) => `${((v / totals.totalUsd) * 100).toFixed(1)}%`;
  const signed = (v: number, digits: number) => `${v >= 0 ? '+' : ''}${v.toFixed(digits)}%`;
  const fmt = (price: number) => price.toFixed(price >= 1 ? 2 : 4);

  const lines: string[] = [];
  lines.push(t('ctx.total', { amount: Math.round(totals.totalUsd).toLocaleString('en-US') }));
  lines.push(t('ctx.classes', { stock: pct(totals.byClass.stock), etf: pct(totals.byClass.etf), fund: pct(totals.byClass.fund), cash: pct(totals.byClass.cash), crypto: pct(totals.byClass.crypto) }));
  if (totals.dayChangeKnownUsd > 0) {
    const dayPct = (totals.dayChangeUsd / (totals.dayChangeKnownUsd - totals.dayChangeUsd)) * 100;
    lines.push(t('ctx.day', { pct: signed(dayPct, 2) }));
  }
  lines.push('');
  lines.push(t('ctx.detail'));

  const label = (a: Asset) => (a.assetClass === 'cash' ? t('ctx.cash', { ccy: a.currency.toUpperCase() }) : `${a.symbol.toUpperCase()} (${a.name}, ${holdingLabel(a, true)})`);
  const rows = assets
    .map((a) => ({ a, v: valueAsset(a, quotes, fx) }))
    .filter((r) => r.v.valueUsd != null)
    .sort((x, y) => (y.v.valueUsd ?? 0) - (x.v.valueUsd ?? 0));

  for (const { a, v } of rows) {
    const parts = [`- ${label(a)}: ${t('ctx.weight', { pct: pct(v.valueUsd ?? 0) })}`];
    // Current price (public data, so no privacy cost): unit price in the quote's currency for securities and crypto,
    // the USD rate for non-USD cash. Lets a report state the level it is reasoning from.
    if (a.assetClass === 'cash') {
      if (a.currency.toUpperCase() !== 'USD' && v.priceUsd != null) parts.push(t('ctx.rate', { ccy: a.currency.toUpperCase(), usd: v.priceUsd.toFixed(4) }));
    } else if (v.quote?.price != null) {
      // The quote time tells a closing report whether this price is the close, and lets any report say how old it is.
      parts.push(t('ctx.price', { price: fmt(v.quote.price), ccy: v.quote.currency, at: `${todayNY(v.quote.asOf).slice(5)} ${formatNYClock(v.quote.asOf)}` }));
    } else if (a.manualPrice != null) {
      parts.push(t('ctx.priceManual', { price: a.manualPrice.toFixed(2) }));
    }
    if (v.quote?.changePct != null && a.assetClass !== 'cash') parts.push(t('ctx.today', { pct: signed(v.quote.changePct, 2) }));
    // The day's own figures come from the user's quote source; a web search rarely turns up an exact open or close.
    if (v.quote && a.assetClass === 'crypto') {
      if (v.quote.low != null && v.quote.high != null) parts.push(t('ctx.range24h', { low: fmt(v.quote.low), high: fmt(v.quote.high) }));
    } else if (v.quote?.freshness === 'nav') {
      if (v.quote.previousClose != null) parts.push(t('ctx.prevNav', { price: fmt(v.quote.previousClose) }));
    } else if (v.quote && a.assetClass !== 'cash') {
      if (v.quote.open != null) parts.push(t('ctx.open', { price: fmt(v.quote.open) }));
      if (v.quote.low != null && v.quote.high != null) parts.push(t('ctx.range', { low: fmt(v.quote.low), high: fmt(v.quote.high) }));
      if (v.quote.previousClose != null) parts.push(t('ctx.prevClose', { price: fmt(v.quote.previousClose) }));
    }
    if (v.costUsd != null && v.costUsd > 0 && v.valueUsd != null) parts.push(t('ctx.pnl', { pct: signed(((v.valueUsd - v.costUsd) / v.costUsd) * 100, 1) }));
    if (a.purchaseDate) parts.push(t('ctx.since', { date: a.purchaseDate }));
    if (a.note) parts.push(t('ctx.note', { note: a.note }));
    lines.push(parts.join(', '));
  }
  // Holdings without a price still count as holdings: name them, so a report covers them instead of never hearing of them.
  const unpriced = assets.filter((a) => valueAsset(a, quotes, fx).valueUsd == null);
  if (unpriced.length > 0) {
    lines.push('');
    lines.push(t('ctx.unpriced', { n: unpriced.length }));
    for (const a of unpriced) lines.push(`- ${label(a)}`);
  }
  return lines.join('\n');
}

export const PRESET_IDS = ['concentration', 'action', 'missing', 'stress'] as const;
export type PresetId = (typeof PRESET_IDS)[number];

export function presetLabel(id: PresetId): string {
  return t(`preset.${id}.label` as const);
}
export function presetText(id: PresetId): string {
  return t(`preset.${id}.text` as const);
}
